import React, { createContext, ReactNode, useContext, useState, useRef, useEffect } from 'react';
import { uploadFiles as uploadFilesApiService, deleteFolder as deleteFolderApiService, deleteFile as deleteFileApiService, CLIENTS_FOLDER_SEGMENT } from '../api/fileService';
import { QuoteContext } from './quoteContext';
import { createLogger } from '../utils/logger';

const fileLogger = createLogger('FILE_CONTEXT');

interface FileContextType {
    files: Map<string, File[]>;
    setFiles: (key: string, files: File[]) => void;
    initializeFilesMap: (initialFiles: Map<string, File[]>) => void;
    getFiles: (key: string) => File[];
    resetFiles: () => void;
    resetFilesByKey: (key: string) => void;
    uploadFiles: (fileUploadKeysFromPage: string[], region: string) => Promise<Map<string, string>>;
    hasFiles: () => boolean;
    hasChangedFiles: (key: string) => boolean;
    changedKeys: Set<string>;
    loading: boolean;
    error: string | null;
}

export const FileContext = createContext<FileContextType>({
    files: new Map<string, File[]>(),
    setFiles: (_key: string, _files: File[]) => { },
    initializeFilesMap: (_initialFiles: Map<string, File[]>) => { },
    getFiles: () => [],
    resetFiles: () => { },
    resetFilesByKey: (_key: string) => { },
    uploadFiles: async (_fileUploadKeys: string[], _region: string) => new Map<string, string>(),
    hasFiles: () => false,
    hasChangedFiles: (_key: string) => false,
    changedKeys: new Set<string>(),
    loading: false,
    error: null,
});

interface FileProviderProps {
    children: ReactNode;
}

export const FileProvider: React.FC<FileProviderProps> = ({ children }) => {
    const [filesMap, setFilesMapInternal] = useState<Map<string, File[]>>(new Map());
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [changedKeys, setChangedKeysState] = useState<Set<string>>(new Set());

    // Guarda el estado de los archivos tal como estaban en la última interacción exitosa con el servidor para esa clave.
    const lastServerSyncedFilesMapRef = useRef<Map<string, File[]>>(new Map());
    // Rutas de archivos conocidas por clave (devueltas por el backend) para eliminaciones precisas
    const lastServerPathsByKeyRef = useRef<Map<string, string[]>>(new Map());
    const { quoteID } = useContext(QuoteContext);

    const filesArraysAreEqual = (filesA: File[], filesB: File[]): boolean => {
        if (filesA.length !== filesB.length) return false;
        // Ordenar por nombre como un intento de comparación canónica, aunque File objects son más complejos.
        // Una comparación más robusta podría considerar más atributos o un hash si fuera necesario.
        const sortedA = [...filesA].sort((a, b) => a.name.localeCompare(b.name));
        const sortedB = [...filesB].sort((a, b) => a.name.localeCompare(b.name));
        for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i].name !== sortedB[i].name || sortedA[i].size !== sortedB[i].size) {
                return false;
            }
        }
        return true;
    };

    // Efecto para actualizar changedKeys cuando filesMap cambia, comparando con el estado base.
    useEffect(() => {
        const newChangedKeys = new Set<string>();
        const allConsideredKeys = new Set([...filesMap.keys(), ...lastServerSyncedFilesMapRef.current.keys()]);

        allConsideredKeys.forEach(key => {
            const currentFiles = filesMap.get(key) || [];
            const syncedFiles = lastServerSyncedFilesMapRef.current.get(key) || [];
            if (!filesArraysAreEqual(currentFiles, syncedFiles)) {
                newChangedKeys.add(key);
            }
        });

        setChangedKeysState(prevChangedKeys => {
            if (newChangedKeys.size === prevChangedKeys.size && Array.from(newChangedKeys).every(k => prevChangedKeys.has(k))) {
                return prevChangedKeys; // No hay cambio en el conjunto de claves cambiadas
            }
            return newChangedKeys;
        });
    }, [filesMap]); // Solo depende de filesMap. lastServerSyncedFilesMapRef.current se lee, no es una dependencia directa para re-ejecutar.


    const setFiles = (key: string, newFiles: File[]) => {
        setFilesMapInternal(prevMap => {
            const map = new Map(prevMap);
            map.set(key, newFiles);
            return map;
        });
        // El useEffect anterior se encargará de actualizar changedKeys
    };

    const initializeFilesMap = (initialFileEntries: Map<string, File[]>) => {
        fileLogger.info('initializeFilesMap called with:', initialFileEntries);
        setFilesMapInternal(new Map(initialFileEntries));
        lastServerSyncedFilesMapRef.current = new Map(initialFileEntries); // Establece el estado base
        lastServerPathsByKeyRef.current = new Map();
        setChangedKeysState(new Set()); // Después de la inicialización, ninguna clave se considera "cambiada" aún.
    };

    const getFiles = (key: string): File[] => filesMap.get(key) || [];

    const resetFiles = () => {
        fileLogger.info('resetFiles called.');
        setFilesMapInternal(new Map());
        lastServerSyncedFilesMapRef.current = new Map(); // También resetea el estado base
        lastServerPathsByKeyRef.current = new Map();
        setChangedKeysState(new Set());
    };

    const resetFilesByKey = (key: string) => {
        fileLogger.info(`resetFilesByKey called for: ${key}`);
        setFilesMapInternal(prevMap => {
            const newMap = new Map(prevMap);
            newMap.delete(key);
            return newMap;
        });
        lastServerPathsByKeyRef.current.delete(key);
        // El useEffect se encargará de actualizar changedKeys en relación con lastServerSyncedFilesMapRef
    };

    const hasFiles = (): boolean => { // Verifica si hay archivos activos seleccionados por el usuario
        if (filesMap.size === 0) return false;
        for (const files of filesMap.values()) {
            if (files.length > 0) return true;
        }
        return false;
    };

    const hasChangedFiles = (key: string): boolean => changedKeys.has(key);

    const handleUploadFiles = async (fileUploadKeysFromPage: string[], region: string): Promise<Map<string, string>> => {
        fileLogger.info('handleUploadFiles started. Keys from page:', fileUploadKeysFromPage, 'Current changedKeys:', Array.from(changedKeys));
        setLoading(true);
        setError(null);
        const resultMap = new Map<string, string>();

        const keysToUploadNewFiles: string[] = [];
        const keysForServerDeletionOnly: string[] = [];

        // Determinar acciones solo para las claves que han cambiado.
        changedKeys.forEach(key => {
            const currentFileObjects = filesMap.get(key) || []; // Archivos en la UI para esta clave
            const serverHadFiles = (lastServerSyncedFilesMapRef.current.get(key) || []).length > 0; // ¿Había archivos en el servidor para esta clave?

            if (currentFileObjects.length > 0) {
                // Hay archivos en la UI. Si son diferentes al base, se deben subir.
                keysToUploadNewFiles.push(key);
            } else if (serverHadFiles) {
                // No hay archivos en la UI, pero sí había en el servidor. Significa que el usuario los borró.
                keysForServerDeletionOnly.push(key);
            }
        });

        fileLogger.debug('Upload - QuoteID:', quoteID,
            'Keys determined for new file upload:', keysToUploadNewFiles,
            'Keys determined for server-side deletion only:', keysForServerDeletionOnly
        );

        if (keysToUploadNewFiles.length === 0 && keysForServerDeletionOnly.length === 0) {
            fileLogger.info('No files identified in changedKeys requiring upload or deletion.');
            setLoading(false);
            return resultMap;
        }

        const newSyncedRef = new Map(lastServerSyncedFilesMapRef.current);

        try {
            // Fase 1: Eliminaciones en el servidor
            // (para claves que tendrán nuevos archivos Y para claves explícitamente limpiadas por el usuario)
            const allKeysInvolvedInDeletion = new Set([...keysToUploadNewFiles, ...keysForServerDeletionOnly]);
            for (const key of allKeysInvolvedInDeletion) {
                const [groupID, questionID] = key.split('__');
                const folderPath = `${region}${CLIENTS_FOLDER_SEGMENT}/${quoteID}/${groupID}/${questionID}`;
                const knownPaths = lastServerPathsByKeyRef.current.get(key) || [];
                if (knownPaths.length > 0) {
                    fileLogger.info(`Attempting to delete ${knownPaths.length} known file(s) for key ${key}`);
                    for (const path of knownPaths) {
                        try {
                            await deleteFileApiService({ path });
                            fileLogger.info(`Deleted file: ${path}`);
                        } catch (deleteFileError) {
                            fileLogger.error(`Error deleting file ${path} for key ${key}:`, deleteFileError);
                            setError(prev => `${prev ? prev + '; ' : ''}Error deleting file for ${key}`);
                        }
                    }
                    // Limpiar rutas conocidas para esta clave tras eliminación
                    lastServerPathsByKeyRef.current.set(key, []);
                } else {
                    fileLogger.info(`No known file paths for key ${key}. Fallback to delete folder: ${folderPath}`);
                    try {
                        await deleteFolderApiService({ path: folderPath });
                        fileLogger.info(`Server folder deleted or did not exist for key ${key}: ${folderPath}`);
                    } catch (deleteError) {
                        fileLogger.error(`Error deleting server folder for key ${key}:`, deleteError);
                        // Continuar incluso si la eliminación falla, para intentar subir nuevos archivos si es el caso.
                        setError(prev => `${prev ? prev + '; ' : ''}Error deleting for ${key}`);
                    }
                }
            }

            // Fase 2: Subida de nuevos archivos
            for (const key of keysToUploadNewFiles) {
                const filesToUpload = filesMap.get(key)!; // Sabemos que existen y tienen >0 elementos por la lógica anterior.

                // Omitir si todos los archivos son de tamaño 0 (placeholders vacíos)
                if (filesToUpload.length > 0 && filesToUpload.every(f => f.size === 0)) {
                    fileLogger.info(`Skipping upload for key ${key} as all selected files have size 0.`);
                    continue;
                }

                const [groupID, questionID] = key.split('__');
                fileLogger.info(`Uploading ${filesToUpload.length} files for key ${key} (groupID: ${groupID}, questionID: ${questionID})`);
                try {
                    const response = await uploadFilesApiService({
                        quoteID,
                        groupID,
                        questionID,
                        files: filesToUpload,
                        region
                    });
                    fileLogger.info(`Files uploaded successfully for ${key}. Paths:`, response.data.paths);
                    resultMap.set(key, response.data.paths);
                    // Guardar rutas reales de archivos devueltas por el backend para futuras eliminaciones precisas
                    const returnedFilePaths = Array.isArray(response.data?.files)
                        ? response.data.files.map(f => f.path).filter(Boolean)
                        : (response.data?.paths ? [response.data.paths] : []);
                    lastServerPathsByKeyRef.current.set(key, returnedFilePaths);
                    // Actualizar el baseline para esta clave tras subida exitosa
                    newSyncedRef.set(key, [...filesToUpload]); // Guardar una copia de los archivos subidos
                } catch (uploadError) {
                    fileLogger.error(`Error uploading files for ${key}:`, uploadError);
                    setError(prev => `${prev ? prev + '; ' : ''}Error uploading for ${key}`);
                    // Si la subida falla, NO actualizamos su baseline en newSyncedRef. Permanecerá "changed".
                }
            }

            // Fase 3: Actualizar baseline para claves donde solo ocurrió una eliminación (y no una subida posterior)
            for (const key of keysForServerDeletionOnly) {
                // Si esta clave no tuvo una subida exitosa (no está en resultMap),
                // significa que la acción fue solo borrarla. Su baseline debe reflejar "sin archivos".
                if (!resultMap.has(key)) {
                    newSyncedRef.set(key, []); // O newSyncedRef.delete(key) si se prefiere.
                    lastServerPathsByKeyRef.current.set(key, []);
                }
            }

            lastServerSyncedFilesMapRef.current = newSyncedRef; // Aplicar todos los cambios al ref base de una vez.

            // Re-calcular changedKeys basado en el nuevo estado base.
            // Esto es importante porque algunas subidas podrían haber fallado,
            // y esas claves deberían permanecer en changedKeys.
            const finalChangedKeys = new Set<string>();
            const allManagedKeys = new Set([...filesMap.keys(), ...lastServerSyncedFilesMapRef.current.keys()]);
            allManagedKeys.forEach(key => {
                if (!filesArraysAreEqual(filesMap.get(key) || [], lastServerSyncedFilesMapRef.current.get(key) || [])) {
                    finalChangedKeys.add(key);
                }
            });
            setChangedKeysState(finalChangedKeys); // Actualizar el estado de changedKeys

            fileLogger.info('Upload process completed. ResultMap:', resultMap, 'New changedKeys:', finalChangedKeys);
            return resultMap;

        } catch (generalError) {
            const errorMessage = generalError instanceof Error ? generalError.message : 'General error in handleUploadFiles';
            fileLogger.error('General error during handleUploadFiles:', generalError);
            setError(errorMessage);
            lastServerSyncedFilesMapRef.current = newSyncedRef; // Actualizar ref incluso si hay error general
            // Re-calcular changedKeys
            const finalChangedKeysOnError = new Set<string>();
            const allManagedKeysOnError = new Set([...filesMap.keys(), ...lastServerSyncedFilesMapRef.current.keys()]);
            allManagedKeysOnError.forEach(key => {
                if (!filesArraysAreEqual(filesMap.get(key) || [], lastServerSyncedFilesMapRef.current.get(key) || [])) {
                    finalChangedKeysOnError.add(key);
                }
            });
            setChangedKeysState(finalChangedKeysOnError);
            throw generalError; // Re-lanzar para que el llamador lo maneje
        } finally {
            setLoading(false);
        }
    };

    return (
        <FileContext.Provider
            value={{
                files: filesMap,
                setFiles,
                initializeFilesMap,
                getFiles,
                resetFiles,
                resetFilesByKey,
                uploadFiles: handleUploadFiles,
                hasFiles,
                hasChangedFiles,
                changedKeys,
                loading,
                error,
            }}
        >
            {children}
        </FileContext.Provider>
    );
};

// Hook personalizado para usar el contexto de archivos
export const useFiles = () => useContext(FileContext);